import moment from 'moment-timezone';
import {
    CustomerAddressDto,
    CustomerDto,
    EmergencyContactDto,
    SaveCustomerDto
} from 'actions/customer/CustomerDtos';
import { convertToIntakeFormAnswerDto } from 'actions/intakeForm/IntakeFormConverter';
import { convertToPet, convertToSavePetDto } from 'actions/pets/PetsConverter';
import { Customer, CustomerAddress, EmergencyContact } from 'types/Customer';

const convertToCustomerAddress = (dto: CustomerAddressDto): CustomerAddress => ({
    id: dto.id,
    addressLineOne: dto.address_line_one,
    addressLineTwo: dto.address_line_two,
    city: dto.city,
    state: dto.state,
    zipCode: dto.zipcode,
    country: dto.country,
    active: dto.active,
    main: dto.main === true
});

export const convertToCustomer = (dto: CustomerDto): Customer => ({
    id: dto.id,

    email: dto.email,
    name: dto.first_name,
    lastName: dto.last_name,
    phone: dto.phone ?? undefined,

    lead: dto.lead,
    active: dto.active,
    blocked: dto.blocked,

    depositEligible: dto.deposit_eligible ?? false,

    token: dto.token,
    channel: dto.channel,

    clientSince: dto.client_since ?? null,

    smsMarketingOptIn: dto.marketing_optout === undefined ? undefined : !dto.marketing_optout,

    addresses: dto.addresses?.map(convertToCustomerAddress) ?? [],

    pets: dto.pets?.map(pet => convertToPet(pet, dto.id)).filter(pet => !!pet) ?? []
});

export const convertToSaveCustomerDto = (
    customer: Customer,
    marketplaceId: number,
    channel: string,
    password?: string
): SaveCustomerDto => {
    return {
        active: customer.active,
        blocked: customer.blocked,

        regenerate_password: false,
        terms_of_service: true,
        phone_optout: false,
        email_optout: false,

        marketing_optout: !customer.smsMarketingOptIn,
        ...(customer.smsMarketingOptIn
            ? { marketing_opt_in_at: moment().toISOString() }
            : { marketing_opt_out_at: moment().toISOString() }),

        ...(password && {
            password: password,
            password_confirmation: password
        }),

        first_name: customer.name,
        last_name: customer.lastName,
        email: customer.email,
        phone: customer.phone?.replace(/\D+/g, '') ?? null,

        channel: channel,
        marketplace_id: marketplaceId,

        lead_source: customer.leadSource,
        lead_stage: customer.leadStage,
        client_since: customer.clientSince ?? null,
        conversion_source: customer.conversionSource ?? null,

        pets_attributes: customer.pets?.map(pet => convertToSavePetDto(pet, marketplaceId)),
        addresses_attributes:
            customer.addresses?.map(address =>
                convertToCustomerAddressDto(address, marketplaceId)
            ) ?? [],
        authorized_contacts_attributes:
            customer.emergencyContacts?.map(convertToCustomerEmergencyContactDto) ?? [],

        custom_question_responses_attributes:
            customer.answers?.map(convertToIntakeFormAnswerDto) || []
    };
};

export const convertToCustomerAddressDto = (
    address: CustomerAddress,
    marketplaceId: number
): CustomerAddressDto => {
    return {
        ...(address.id && { id: address.id }),
        marketplace_id: marketplaceId,
        address_line_one: address.addressLineOne,
        address_line_two: address.addressLineTwo,
        city: address.city,
        state: address.state,
        zipcode: address.zipCode,
        country: address.country,
        active: address.active,
        main: true
    };
};

export const convertToCustomerEmergencyContactDto = (
    contact: EmergencyContact
): EmergencyContactDto => {
    return {
        uuid: contact.uuid,
        first_name: contact.name,
        last_name: contact.lastName,
        phone: contact.phone,
        relationship_id: contact.relationship.id,
        emergency_contact: true
    };
};
